/**
 * Traffic's Source → Campaign (utm_campaign) → Content (utm_content) tree,
 * built from /traffic/sub-source-funnel rows. The Traffic page and the
 * Channels report's expandable rows both build it here, so they group one way.
 *
 * `groupOf` picks a row's top level: the first-touch source on Traffic, the
 * canonical channel on Channels. Children come back unsorted.
 */

// Decode URL-encoded UTM parameters
export function decodeUTM(value) {
  if (!value) return 'direct'
  try {
    // Decode URL encoding (handles %20, +, %2B, etc.)
    let decoded = decodeURIComponent(value.replace(/\+/g, ' '))
    // Clean up common patterns
    decoded = decoded
      .replace(/\s+/g, ' ')  // Multiple spaces to single
      .trim()
    return decoded || 'direct'
  } catch (e) {
    return value // Return original if decoding fails
  }
}

export function buildSourceTree(subSources, getSourceName, groupOf = row => row.source || 'other') {
  if (!subSources) return null
  
  // Group by source category, then by campaign
  const bySource = {}
  
  subSources.forEach(row => {
    const sourceId = groupOf(row)
    const sourceName = getSourceName(sourceId)
    const campaignName = decodeUTM(row.sub_source)
    const utmCampaign = row.utm_campaign ? decodeUTM(row.utm_campaign) : null
    
    // For Meta/Google, sub_source is often utm_content, and we have utm_campaign separately
    // For affiliates, sub_source is the affiliate ID
    const hasUtmCampaign = utmCampaign && utmCampaign !== campaignName
    
    if (!bySource[sourceId]) {
      bySource[sourceId] = {
        id: sourceId,
        sourceId: sourceId,
        name: sourceName,
        isSource: true,
        visitors: 0,
        emails: 0,
        payments: 0,
        pageviews: 0,
        children: {},
      }
    }
    
    // Aggregate to source
    bySource[sourceId].visitors += row.visitors || 0
    bySource[sourceId].emails += row.emails || 0
    bySource[sourceId].payments += row.payments || 0
    bySource[sourceId].pageviews += row.pageviews || 0
    
    // For Everflow/Katalys: Affiliate ID → sub1 hierarchy
    const isAffiliateSource = sourceId === 'everflow' || sourceId === 'katalys'
    const sub1Value = row.sub1 ? decodeUTM(row.sub1) : null
    
    if (isAffiliateSource) {
      // First level: Affiliate ID (sub_source = affid/aid)
      if (!bySource[sourceId].children[campaignName]) {
        bySource[sourceId].children[campaignName] = {
          id: `${sourceId}-${row.sub_source}`,
          name: campaignName,
          fullName: campaignName,
          sourceId: sourceId,
          isAffiliateId: true,
          visitors: 0,
          emails: 0,
          payments: 0,
          pageviews: 0,
          children: {},
        }
      }
      
      bySource[sourceId].children[campaignName].visitors += row.visitors || 0
      bySource[sourceId].children[campaignName].emails += row.emails || 0
      bySource[sourceId].children[campaignName].payments += row.payments || 0
      bySource[sourceId].children[campaignName].pageviews += row.pageviews || 0
      
      // Second level: sub1 values (use "not_set" for traffic without sub1)
      const sub1Key = sub1Value || '(not set)'
      if (!bySource[sourceId].children[campaignName].children[sub1Key]) {
        bySource[sourceId].children[campaignName].children[sub1Key] = {
          id: `${sourceId}-${row.sub_source}-${sub1Key}`,
          name: sub1Key,
          fullName: sub1Value ? `sub1: ${sub1Value}` : '(no sub1)',
          sourceId: sourceId,
          isSub1: true,
          isNotSet: !sub1Value,
          visitors: 0,
          emails: 0,
          payments: 0,
          pageviews: 0,
        }
      }
      
      bySource[sourceId].children[campaignName].children[sub1Key].visitors += row.visitors || 0
      bySource[sourceId].children[campaignName].children[sub1Key].emails += row.emails || 0
      bySource[sourceId].children[campaignName].children[sub1Key].payments += row.payments || 0
      bySource[sourceId].children[campaignName].children[sub1Key].pageviews += row.pageviews || 0
    } else if (hasUtmCampaign) {
      // For Meta/Google with utm_campaign: Campaign → Content hierarchy
      if (!bySource[sourceId].children[utmCampaign]) {
        bySource[sourceId].children[utmCampaign] = {
          id: `${sourceId}-${utmCampaign}`,
          name: utmCampaign,
          fullName: utmCampaign,
          sourceId: sourceId,
          isCampaign: true,
          visitors: 0,
          emails: 0,
          payments: 0,
          pageviews: 0,
          children: [],
        }
      }
      
      bySource[sourceId].children[utmCampaign].visitors += row.visitors || 0
      bySource[sourceId].children[utmCampaign].emails += row.emails || 0
      bySource[sourceId].children[utmCampaign].payments += row.payments || 0
      bySource[sourceId].children[utmCampaign].pageviews += row.pageviews || 0
      
      // Add content as child of campaign
      bySource[sourceId].children[utmCampaign].children.push({
        id: `${sourceId}-${utmCampaign}-${row.sub_source}`,
        name: campaignName,
        fullName: campaignName,
        sourceId: sourceId,
        isContent: true,
        visitors: row.visitors || 0,
        emails: row.emails || 0,
        payments: row.payments || 0,
        pageviews: row.pageviews || 0,
      })
    } else {
      // No utm_campaign, just add directly under source
      if (!bySource[sourceId].children[campaignName]) {
        bySource[sourceId].children[campaignName] = {
          id: `${sourceId}-${row.sub_source}`,
          name: campaignName,
          fullName: campaignName,
          sourceId: sourceId,
          visitors: 0,
          emails: 0,
          payments: 0,
          pageviews: 0,
          children: [],
        }
      }
      
      bySource[sourceId].children[campaignName].visitors += row.visitors || 0
      bySource[sourceId].children[campaignName].emails += row.emails || 0
      bySource[sourceId].children[campaignName].payments += row.payments || 0
      bySource[sourceId].children[campaignName].pageviews += row.pageviews || 0
    }
  })
  
  // Convert to arrays; the caller sorts.
  return Object.values(bySource)
    .map(source => ({
      ...source,
      children: Object.values(source.children)
        .map(campaign => {
          // For affiliate sources, children is an object (sub1 values) that needs conversion
          const children = campaign.isAffiliateId && campaign.children && typeof campaign.children === 'object' && !Array.isArray(campaign.children)
            ? Object.values(campaign.children)
            : (campaign.children || [])
          
          return {
            ...campaign,
            children,
          }
        }),
    }))
}
